/**
 * Lazy-loading API for GRE Quant topic exercises
 *
 * This module provides functions to load topic exercises on-demand using
 * dynamic imports, reducing initial bundle size and improving app performance.
 */

// Cache for loaded topics to avoid re-importing
const topicsCache = new Map();

// Cache for the topics index
let topicsIndexCache = null;

/**
 * Dynamic import map for topic files
 * Webpack will code-split these into separate chunks
 */
const topicImports = {
  algebra: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/algebra.json'),
  linear_quadratic: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/linear_quadratic.json'),
  number_properties: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/number_properties.json'),
  roots: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/roots.json'),
  exponents: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/exponents.json'),
  inequalities: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/inequalities.json'),
  geometry: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/geometry.json'),
  coordinate_geometry: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/coordinate_geometry.json'),
  sequences: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/sequences.json'),
  data_analysis_problems: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/data_analysis_problems.json'),
  data_interpretation_questions: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/data_interpretation_questions.json'),
  overlapping_sets: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/overlapping_sets.json'),
  absolute_value: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/absolute_value.json'),
  general_word_problems: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/general_word_problems.json'),
  rates: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/rates.json'),
  combinations_permutations: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/combinations_permutations.json'),
  probability: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/probability.json'),
  functions: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/functions.json'),
  work_problems: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/work_problems.json'),
  unit_conversions: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/unit_conversions.json'),
  ratios_percents: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/ratios_percents.json'),
  statistics: () =>
    import('../../../../assets/mock-data/gre-quant-exercises/statistics.json'),
};

export const questionToEtsTypeCode = (q) => {
  const t = q?.questionType || 'multiple_choice_single';
  if (t === 'quantitative_comparison') return 'QC';
  if (t === 'multiple_choice_single') return 'MC1';
  if (t === 'multiple_choice_multi') return 'MCM';
  if (t === 'numeric_entry') return 'NE';
  if (t === 'data_interpretation') {
    if (Array.isArray(q?.correct)) return 'MCM';
    if (q?.correctAnswer !== undefined || Array.isArray(q?.acceptedAnswers))
      return 'NE';
    if (q?.quantityA && q?.quantityB) return 'QC';
    return 'MC1';
  }
  return 'MC1';
};

const filterQuestions = (
  questions,
  {
    difficulty = 'mixed',
    includeImages = true,
    questionTypes = [],
    subconcepts = [],
  } = {}
) => {
  let filtered = [...questions];

  if (
    difficulty &&
    difficulty !== 'mixed' &&
    difficulty !== 'mixed_hard' &&
    difficulty !== 'mixed_sunday'
  ) {
    filtered = filtered.filter((q) => q.difficulty === difficulty);
  }

  if (includeImages === false) {
    filtered = filtered.filter((q) => !q.hasImage);
  }

  if (Array.isArray(subconcepts) && subconcepts.length > 0) {
    const allowedSubconcepts = new Set(subconcepts);
    const bySubconcept = filtered.filter((q) =>
      allowedSubconcepts.has(q.subconcept)
    );
    if (bySubconcept.length > 0) {
      filtered = bySubconcept;
    }
  }

  if (Array.isArray(questionTypes) && questionTypes.length > 0) {
    const byType = filtered.filter((q) =>
      questionTypes.includes(questionToEtsTypeCode(q))
    );
    if (byType.length > 0) {
      filtered = byType;
    }
  }

  return filtered;
};

/**
 * Load the lightweight topics index (no exercises, just metadata)
 * This should be loaded on initial page load for topic selection UI
 * @returns {Promise<Object>} Topics index with metadata and topic info
 */
export const loadTopicsIndex = async () => {
  if (topicsIndexCache) {
    return topicsIndexCache;
  }

  try {
    const module =
      await import('../../../../assets/mock-data/gre-quant-exercises/topics-index.json');
    topicsIndexCache = module.default || module;
    console.log(
      'Loaded topics index:',
      Object.keys(topicsIndexCache.topics).length,
      'topics'
    );
    return topicsIndexCache;
  } catch (error) {
    console.error('Error loading topics index:', error);
    throw error;
  }
};

/**
 * Load exercises for a specific topic
 * @param {string} topicId - The topic identifier (e.g., 'algebra', 'geometry')
 * @returns {Promise<Object>} Topic data with exercises
 */
export const loadTopicExercises = async (topicId) => {
  // Return from cache if available
  if (topicsCache.has(topicId)) {
    console.log(`Returning cached exercises for topic: ${topicId}`);
    return topicsCache.get(topicId);
  }

  try {
    const importFn = topicImports[topicId];
    if (!importFn) {
      throw new Error(`Unknown topic: ${topicId}`);
    }

    const module = await importFn();
    const topicData = module.default || module;
    topicsCache.set(topicId, topicData);
    console.log(
      `Loaded ${topicData.exercises?.length || 0} exercises for topic: ${topicId}`
    );
    return topicData;
  } catch (error) {
    console.error(`Error loading topic ${topicId}:`, error);
    throw error;
  }
};

/**
 * Load exercises for multiple topics in parallel
 * @param {string[]} topicIds - Array of topic identifiers
 * @returns {Promise<Object>} Object mapping topicId to topic data
 */
export const loadMultipleTopics = async (topicIds) => {
  const results = {};
  const loadPromises = [];

  for (const topicId of topicIds) {
    if (topicsCache.has(topicId)) {
      results[topicId] = topicsCache.get(topicId);
    } else {
      loadPromises.push(
        loadTopicExercises(topicId)
          .then((data) => {
            results[topicId] = data;
          })
          .catch((error) => {
            console.error(`Failed to load topic ${topicId}:`, error);
          })
      );
    }
  }

  await Promise.all(loadPromises);
  return results;
};

/**
 * Get available topics from the index
 * @returns {Promise<string[]>} Array of topic IDs
 */
export const getAvailableTopics = async () => {
  const index = await loadTopicsIndex();
  return Object.keys(index.topics || {});
};

/**
 * Get topic metadata (without loading exercises)
 * @param {string} topicId - The topic identifier
 * @returns {Promise<Object|null>} Topic metadata or null if not found
 */
export const getTopicMetadata = async (topicId) => {
  const index = await loadTopicsIndex();
  return index.topics?.[topicId] || null;
};

/**
 * Get available question count for selected topics
 * @param {string[]} topicIds - Array of topic IDs (empty = all topics)
 * @param {string} difficulty - 'easy', 'medium', 'hard', or 'mixed'
 * @returns {Promise<number>} Total question count
 */
export const getAvailableQuestionCount = async (
  topicIds,
  difficulty = 'mixed',
  subconcepts = []
) => {
  const index = await loadTopicsIndex();
  const topics = index.topics || {};
  let count = 0;

  const targetTopics = topicIds.length > 0 ? topicIds : Object.keys(topics);

  if (Array.isArray(subconcepts) && subconcepts.length > 0) {
    const topicsData = await loadMultipleTopics(targetTopics);
    const countForDifficulty = (targetDifficulty) =>
      targetTopics.reduce((sum, topicId) => {
        const exercises = topicsData[topicId]?.exercises || [];
        return (
          sum +
          filterQuestions(exercises, {
            difficulty: targetDifficulty,
            subconcepts,
          }).length
        );
      }, 0);

    if (difficulty === 'mixed_hard') {
      return getMaxMixedHardQuestionCount(
        countForDifficulty('hard'),
        countForDifficulty('very_hard')
      );
    }

    if (difficulty === 'mixed_sunday') {
      return (
        countForDifficulty('medium') +
        countForDifficulty('hard') +
        countForDifficulty('very_hard')
      );
    }

    return targetTopics.reduce((sum, topicId) => {
      const exercises = topicsData[topicId]?.exercises || [];
      return (
        sum +
        filterQuestions(exercises, {
          difficulty,
          subconcepts,
        }).length
      );
    }, 0);
  }

  if (difficulty === 'very_hard') {
    return targetTopics.reduce(
      (sum, topicId) => sum + (topics[topicId]?.difficulties?.very_hard || 0),
      0
    );
  }

  if (difficulty === 'mixed_hard') {
    const hardCount = targetTopics.reduce(
      (sum, topicId) => sum + (topics[topicId]?.difficulties?.hard || 0),
      0
    );
    const veryHardCount = targetTopics.reduce(
      (sum, topicId) => sum + (topics[topicId]?.difficulties?.very_hard || 0),
      0
    );
    return getMaxMixedHardQuestionCount(hardCount, veryHardCount);
  }

  if (difficulty === 'mixed_sunday') {
    return targetTopics.reduce(
      (sum, topicId) =>
        sum +
        (topics[topicId]?.difficulties?.medium || 0) +
        (topics[topicId]?.difficulties?.hard || 0) +
        (topics[topicId]?.difficulties?.very_hard || 0),
      0
    );
  }

  for (const topicId of targetTopics) {
    const topic = topics[topicId];
    if (topic) {
      if (difficulty === 'mixed') {
        count += topic.exerciseCount || 0;
      } else {
        count += topic.difficulties?.[difficulty] || 0;
      }
    }
  }

  return count;
};

/**
 * Count questions matching filters after loading exercise data.
 * Used for accurate "unseen" / "mistakes" availability in the practice hub.
 */
export const countFilteredQuestions = async ({
  topics = [],
  difficulty = 'mixed',
  includeImages = true,
  questionTypes = [],
  subconcepts = [],
  excludeIds = [],
  includeIds = [],
} = {}) => {
  const index = await loadTopicsIndex();
  const allTopicIds = Object.keys(index.topics || {});
  const targetTopics = topics.length > 0 ? topics : allTopicIds;

  if (targetTopics.length === 0) return 0;

  const topicsData = await loadMultipleTopics(targetTopics);
  const excludeSet = toIdSet(excludeIds);
  const includeSet = includeIds.length > 0 ? toIdSet(includeIds) : null;

  const countForDifficulty = (targetDifficulty) => {
    let count = 0;
    for (const topicId of targetTopics) {
      const exercises = topicsData[topicId]?.exercises || [];
      let filtered = filterQuestions(exercises, {
        difficulty: targetDifficulty,
        includeImages,
        questionTypes,
        subconcepts,
      });

      if (includeSet) {
        filtered = filtered.filter((q) => includeSet.has(q.id));
      }

      count += filtered.filter((q) => !excludeSet.has(q.id)).length;
    }
    return count;
  };

  if (difficulty === 'mixed_hard') {
    return countForDifficulty('hard') + countForDifficulty('very_hard');
  }

  if (difficulty === 'mixed_sunday') {
    return (
      countForDifficulty('medium') +
      countForDifficulty('hard') +
      countForDifficulty('very_hard')
    );
  }

  return countForDifficulty(difficulty);
};

export const getMixedHardQuestionSplit = (questionCount) => {
  const total = Math.max(0, Number.parseInt(questionCount, 10) || 0);
  if (total === 0) {
    return { hard: 0, veryHard: 0 };
  }
  if (total === 1) {
    return { hard: 1, veryHard: 0 };
  }

  const hard = Math.min(total - 1, Math.ceil(total * 0.7));
  return {
    hard,
    veryHard: total - hard,
  };
};

export const getMixedDifficultyQuestionSplit = (
  questionCount,
  mix = { medium: 0.7, hard: 0.3, very_hard: 0 }
) => {
  const total = Math.max(0, Number.parseInt(questionCount, 10) || 0);
  if (total === 0) return { medium: 0, hard: 0, very_hard: 0 };

  const safeMix = {
    medium: Math.max(0, Number(mix.medium) || 0),
    hard: Math.max(0, Number(mix.hard) || 0),
    very_hard: Math.max(0, Number(mix.very_hard) || 0),
  };
  const mixTotal =
    safeMix.medium + safeMix.hard + safeMix.very_hard || 1;

  const medium = Math.floor((total * safeMix.medium) / mixTotal);
  const veryHard = Math.floor((total * safeMix.very_hard) / mixTotal);
  const hard = Math.max(0, total - medium - veryHard);

  return { medium, hard, very_hard: veryHard };
};

export const getMaxMixedHardQuestionCount = (hardCount, veryHardCount) => {
  const safeHardCount = Math.max(0, Number.parseInt(hardCount, 10) || 0);
  const safeVeryHardCount = Math.max(
    0,
    Number.parseInt(veryHardCount, 10) || 0
  );

  return safeHardCount + safeVeryHardCount;
};

/**
 * Select `count` questions from `pool` with balanced subconcept distribution.
 *
 * Strategy:
 *   1. Group questions by their `subconcept` field (fallback: '_default').
 *   2. Shuffle each group internally.
 *   3. Round-robin across subconcepts (in random order) until `count` is reached.
 *
 * This guarantees no single subconcept dominates the selection unless the user
 * requests more questions than the other subconcepts can contribute (i.e. they
 * want to review *all* questions of a concept).
 *
 * @param {Array} pool  - The full array of candidate questions (already filtered).
 * @param {number} count - How many questions to pick.
 * @returns {Array} A balanced selection of at most `count` questions.
 */
export const balancedSubconceptSample = (pool, count) => {
  if (!pool || pool.length === 0) return [];

  // If we want everything (or more), just return all shuffled.
  if (count >= pool.length) {
    return [...pool].sort(() => Math.random() - 0.5);
  }

  // 1. Group by subconcept
  const groups = {};
  for (const q of pool) {
    const key = q.subconcept || '_default';
    if (!groups[key]) groups[key] = [];
    groups[key].push(q);
  }

  // 2. Shuffle within each group
  const subconcepts = Object.keys(groups).sort(() => Math.random() - 0.5);
  for (const sc of subconcepts) {
    groups[sc].sort(() => Math.random() - 0.5);
  }

  // 3. Round-robin across subconcepts
  const selected = [];
  const pointers = {};
  for (const sc of subconcepts) pointers[sc] = 0;

  while (selected.length < count) {
    let addedThisRound = false;
    for (const sc of subconcepts) {
      if (selected.length >= count) break;
      if (pointers[sc] < groups[sc].length) {
        selected.push(groups[sc][pointers[sc]]);
        pointers[sc]++;
        addedThisRound = true;
      }
    }
    // All groups exhausted — can't fill further.
    if (!addedThisRound) break;
  }

  return selected;
};

const toIdSet = (ids) => {
  if (ids instanceof Set) return ids;
  if (Array.isArray(ids)) return new Set(ids);
  return new Set();
};

const sampleQuestionsForTopics = ({
  topicsData,
  topicIds,
  difficulty = 'mixed',
  questions = 15,
  includeImages = true,
  topicQuestions = {},
  questionTypes = [],
  subconcepts = [],
  excludeIds = new Set(),
  includeIds = null,
}) => {
  if (!Array.isArray(topicIds) || topicIds.length === 0) return [];

  const excludeSet = toIdSet(excludeIds);
  const includeSet = includeIds ? toIdSet(includeIds) : null;

  let allQuestions = [];

  for (const topicId of topicIds) {
    const topicData = topicsData[topicId];
    if (!topicData?.exercises) continue;

    let topicExercises = filterQuestions(topicData.exercises, {
      difficulty,
      includeImages,
      questionTypes,
      subconcepts,
    });

    if (includeSet) {
      topicExercises = topicExercises.filter((q) => includeSet.has(q.id));
    }

    topicExercises = topicExercises.filter((q) => !excludeSet.has(q.id));

    const questionsPerTopic =
      topicQuestions[topicId] || Math.ceil(questions / topicIds.length);

    const balanced = balancedSubconceptSample(
      topicExercises,
      questionsPerTopic
    ).map((q) => ({ ...q, topic: topicId }));
    allQuestions = [...allQuestions, ...balanced];
  }

  return allQuestions.sort(() => Math.random() - 0.5).slice(0, questions);
};

/**
 * Get questions from loaded topics with filtering
 * @param {Object} config - Configuration object
 * @param {string[]} config.topics - Topic IDs to get questions from
 * @param {string} config.difficulty - 'easy', 'medium', 'hard', or 'mixed'
 * @param {number} config.questions - Number of questions to return
 * @param {boolean} config.includeImages - Whether to include image questions
 * @param {Object} config.topicQuestions - Optional per-topic question counts
 * @returns {Promise<Array>} Array of question objects
 */
export const getQuestionsFromTopics = async (config) => {
  const {
    topics = [],
    difficulty = 'mixed',
    questions = 15,
    includeImages = true,
    topicQuestions = {},
    questionTypes = [],
    subconcepts = [],
    excludeIds = [],
    includeIds = [],
    difficultyMix = null,
  } = config;

  const excludeSet = toIdSet(excludeIds);
  const includeSet = includeIds.length > 0 ? toIdSet(includeIds) : null;
  const sampleParams = {
    includeImages,
    topicQuestions,
    questionTypes,
    subconcepts,
    excludeIds: excludeSet,
    includeIds: includeSet,
  };

  if (difficulty === 'very_hard') {
    const topicsData = await loadMultipleTopics(topics);
    return sampleQuestionsForTopics({
      topicsData,
      topicIds: topics,
      difficulty: 'very_hard',
      questions,
      ...sampleParams,
    });
  }

  if (difficulty === 'mixed_hard') {
    const hardSplit = getMixedHardQuestionSplit(questions);
    const topicsData = await loadMultipleTopics(topics);

    const hardQuestions = sampleQuestionsForTopics({
      topicsData,
      topicIds: topics,
      difficulty: 'hard',
      questions: hardSplit.hard,
      ...sampleParams,
    });

    const selectedIds = new Set(hardQuestions.map((q) => q.id));
    const veryHardQuestions = sampleQuestionsForTopics({
      topicsData,
      topicIds: topics,
      difficulty: 'very_hard',
      questions: questions - hardQuestions.length,
      ...sampleParams,
      excludeIds: new Set([...excludeSet, ...selectedIds]),
    });

    veryHardQuestions.forEach((q) => selectedIds.add(q.id));

    const backfillHardQuestions = sampleQuestionsForTopics({
      topicsData,
      topicIds: topics,
      difficulty: 'hard',
      questions: questions - hardQuestions.length - veryHardQuestions.length,
      ...sampleParams,
      excludeIds: new Set([...excludeSet, ...selectedIds]),
    });

    return [...hardQuestions, ...veryHardQuestions, ...backfillHardQuestions]
      .sort(() => Math.random() - 0.5)
      .slice(0, questions);
  }

  if (difficulty === 'mixed_sunday') {
    const split = getMixedDifficultyQuestionSplit(questions, difficultyMix);
    const topicsData = await loadMultipleTopics(topics);
    const selectedIds = new Set();
    const selectedQuestions = [];

    for (const [targetDifficulty, targetCount] of Object.entries(split)) {
      if (targetCount <= 0) continue;
      const nextQuestions = sampleQuestionsForTopics({
        topicsData,
        topicIds: topics,
        difficulty: targetDifficulty,
        questions: targetCount,
        ...sampleParams,
        excludeIds: new Set([...excludeSet, ...selectedIds]),
      });
      nextQuestions.forEach((q) => selectedIds.add(q.id));
      selectedQuestions.push(...nextQuestions);
    }

    if (selectedQuestions.length < questions) {
      const backfillQuestions = sampleQuestionsForTopics({
        topicsData,
        topicIds: topics,
        difficulty: 'hard',
        questions: questions - selectedQuestions.length,
        ...sampleParams,
        excludeIds: new Set([...excludeSet, ...selectedIds]),
      });
      selectedQuestions.push(...backfillQuestions);
    }

    return selectedQuestions.sort(() => Math.random() - 0.5).slice(0, questions);
  }

  const topicsData = await loadMultipleTopics(topics);
  return sampleQuestionsForTopics({
    topicsData,
    topicIds: topics,
    difficulty,
    questions,
    ...sampleParams,
  });
};

/**
 * Clear the topics cache (useful for testing or forced refresh)
 */
export const clearTopicsCache = () => {
  topicsCache.clear();
  topicsIndexCache = null;
  console.log('Topics cache cleared');
};

/**
 * Preload topics that are likely to be used
 * Call this to warm up the cache for better UX
 * @param {string[]} topicIds - Topics to preload
 */
export const preloadTopics = async (topicIds) => {
  console.log('Preloading topics:', topicIds);
  await loadMultipleTopics(topicIds);
};

/**
 * Get all exercises from cache (for components that need immediate access)
 * Returns null if topics haven't been loaded yet
 * @param {string} topicId - Topic ID
 * @returns {Array|null} Exercises array or null
 */
export const getCachedExercises = (topicId) => {
  const cached = topicsCache.get(topicId);
  return cached?.exercises || null;
};

/**
 * Check if a topic is already cached
 * @param {string} topicId - Topic ID
 * @returns {boolean}
 */
export const isTopicCached = (topicId) => {
  return topicsCache.has(topicId);
};

/**
 * Get list of all available topic IDs (synchronous, from import map)
 * @returns {string[]}
 */
export const getAllTopicIds = () => {
  return Object.keys(topicImports);
};
